import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  Button,
  Checkbox,
  DateRangePicker,
  SearchSelect,
  type DatePreset,
} from '@/components/ui'
import { useOrderCustomerSearch } from '../api/queries'
import type { OpenOrdersFilter } from '../api/types'

export interface OpenOrdersFilterRailProps {
  filter: OpenOrdersFilter
  onChange: (next: OpenOrdersFilter) => void
  collapsed: boolean
  onToggleCollapsed: () => void
  /** For the date picker preset button highlighting. */
  preset: DatePreset
  onPresetChange: (p: DatePreset) => void
  savedSearchSlot?: React.ReactNode
  /** 2026-07-21 — Order History floor (outside_sales_rep). Passed straight
   *  through to `DateRangePicker`; undefined for every other role. */
  minDate?: string
}

/**
 * Customer filter. Sets `filter.partner_ids`, which the report applies
 * SERVER-side — the page's search box only narrows rows already fetched,
 * which doesn't hold up on a history window (3,933 lines over 20 months in
 * production). The endpoint returns commercial parents, so picking one
 * covers every child ship-to under that account.
 */
function CustomerFilter({
  filter,
  onChange,
}: Pick<OpenOrdersFilterRailProps, 'filter' | 'onChange'>) {
  const [query, setQuery] = useState('')
  const [label, setLabel] = useState<string | null>(null)
  const { data, isFetching } = useOrderCustomerSearch(query)

  const selectedId = filter.partner_ids?.[0]

  return (
    <SearchSelect
      items={(data ?? []).map((c) => ({ value: String(c.id), label: c.name }))}
      value={selectedId ? String(selectedId) : undefined}
      selectedLabel={label}
      placeholder="Any customer"
      emptyMessage={
        isFetching ? 'Searching…' : query ? 'No customers found' : 'Type to search'
      }
      onQueryChange={setQuery}
      onSelect={(v) => {
        const picked = (data ?? []).find((c) => String(c.id) === v)
        setLabel(picked?.name ?? null)
        onChange({ ...filter, partner_ids: [Number(v)] })
      }}
      onClear={() => {
        setLabel(null)
        const { partner_ids: _drop, ...rest } = filter
        onChange(rest)
      }}
    />
  )
}

/**
 * Filter rail per spec §DataView. Collapsed default (per manifest) —
 * collapses to a 32px strip showing only the active-filter count badge
 * and a toggle.
 *
 * v1 filters: date range, outstanding-only, overdue-only, exclusions.
 * Customer multi-select + product filter come in when the respective
 * endpoints stabilize (post-M6).
 */
export function OpenOrdersFilterRail({
  filter,
  onChange,
  collapsed,
  onToggleCollapsed,
  preset,
  onPresetChange,
  savedSearchSlot,
  minDate,
}: OpenOrdersFilterRailProps) {
  const rules = filter.rules ?? {}
  const exclusions = filter.exclusions ?? {}

  const activeCount =
    ((filter.partner_ids?.length ?? 0) > 0 ? 1 : 0) +
    (rules.outstanding_only ? 1 : 0) +
    (rules.overdue_only ? 1 : 0) +
    (exclusions.exclude_samples ? 1 : 0) +
    (exclusions.exclude_shipping ? 1 : 0) +
    (exclusions.exclude_services ? 1 : 0)

  if (collapsed) {
    return (
      <aside
        className="flex w-8 flex-col items-center border-r border-border-subtle bg-surface-sunk"
        aria-label="Filters (collapsed)"
      >
        <button
          type="button"
          aria-label="Expand filters"
          onClick={onToggleCollapsed}
          className="mt-2 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronRight size={14} />
        </button>
        {activeCount > 0 ? (
          <span className="mt-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-subtle px-1.5 text-2xs font-medium text-accent-text">
            {activeCount}
          </span>
        ) : null}
      </aside>
    )
  }

  return (
    <aside
      aria-label="Filters"
      className="flex w-[var(--layout-filter-rail-width)] flex-col gap-3 overflow-y-auto border-r border-border-subtle bg-surface-sunk p-3"
    >
      <header className="flex items-center justify-between border-b border-border-subtle pb-2">
        <span className="text-role-section-label">
          Filters
          {activeCount > 0 ? (
            <span className="ml-1.5 font-sans text-accent-text">· {activeCount}</span>
          ) : null}
        </span>
        <button
          type="button"
          aria-label="Collapse filters"
          onClick={onToggleCollapsed}
          className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronLeft size={14} />
        </button>
      </header>

      <Section label="Date range">
        <DateRangePicker
          preset={preset}
          value={{ from: filter.date_from, to: filter.date_to }}
          minDate={minDate}
          onChange={(r, p) => {
            onPresetChange(p)
            onChange({ ...filter, date_from: r.from, date_to: r.to })
          }}
        />
      </Section>

      <Section label="Customer">
        <CustomerFilter filter={filter} onChange={onChange} />
      </Section>

      <Section label="Rules">
        <Checkbox
          label="Outstanding only"
          checked={rules.outstanding_only ?? false}
          onChange={(e) =>
            onChange({
              ...filter,
              rules: { ...rules, outstanding_only: e.target.checked },
            })
          }
        />
        <Checkbox
          label="Overdue only"
          checked={rules.overdue_only ?? false}
          onChange={(e) =>
            onChange({
              ...filter,
              rules: { ...rules, overdue_only: e.target.checked },
            })
          }
        />
      </Section>

      <Section label="Exclusions">
        <Checkbox
          label="Exclude samples"
          checked={exclusions.exclude_samples ?? false}
          onChange={(e) =>
            onChange({
              ...filter,
              exclusions: { ...exclusions, exclude_samples: e.target.checked },
            })
          }
        />
        <Checkbox
          label="Exclude shipping"
          checked={exclusions.exclude_shipping ?? false}
          onChange={(e) =>
            onChange({
              ...filter,
              exclusions: { ...exclusions, exclude_shipping: e.target.checked },
            })
          }
        />
        <Checkbox
          label="Exclude services"
          checked={exclusions.exclude_services ?? false}
          onChange={(e) =>
            onChange({
              ...filter,
              exclusions: { ...exclusions, exclude_services: e.target.checked },
            })
          }
        />
      </Section>

      {savedSearchSlot ? (
        <Section label="Saved searches">{savedSearchSlot}</Section>
      ) : null}

      <footer className="mt-auto flex flex-col gap-1.5 border-t border-border-subtle pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            onChange({
              date_from: filter.date_from,
              date_to: filter.date_to,
              rules: {},
              exclusions: {},
            })
          }
        >
          Clear all
        </Button>
      </footer>
    </aside>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-role-section-label">
        {label}
      </span>
      {children}
    </div>
  )
}
